import React from "react";
import { Handle, Position } from "@xyflow/react";
import {
  Play, Square, GitBranch, Zap, Clock, Users, AlertTriangle,
} from "lucide-react";

/**
 * Custom React Flow nodes.
 *
 * Each type is visually distinct at a glance, because the whole point of drawing
 * the process is that someone can look at it and see the shape: where work sits
 * (stage), where it branches (decision), what the system does on its own
 * (automation), and how it ends.
 */

const PORT = { width: 9, height: 9, background: "var(--cares-surface-raised)", border: "2px solid var(--cares-line-card)" };

const Shell = ({ selected, accent, children, width = 190 }) => (
  <div
    style={{
      width,
      borderRadius: 12,
      background: "var(--cares-surface-card)",
      border: `1.5px solid ${selected ? accent : "var(--cares-text-note)"}`,
      boxShadow: selected
        ? `0 0 0 3px ${accent}22, 0 6px 16px rgba(15,23,42,.10)`
        : "0 1px 3px rgba(15,23,42,.08)",
      overflow: "hidden",
      transition: "box-shadow .15s, border-color .15s",
    }}
  >
    <div style={{ height: 3, background: accent }} />
    <div style={{ padding: "9px 11px" }}>{children}</div>
  </div>
);

const Title = ({ icon: Icon, color, children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
    <Icon size={13} style={{ color, flexShrink: 0 }} />
    <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--cares-text-1)",
                   overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {children}
    </span>
  </div>
);

const Meta = ({ children }) => (
  <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>{children}</div>
);

const Chip = ({ children, tone = "var(--cares-text-3)", color = "var(--cares-text-3)" }) => (
  <span style={{ background: tone, color, fontSize: 9.5, fontWeight: 600,
                 padding: "2px 6px", borderRadius: 5, letterSpacing: .2 }}>
    {children}
  </span>
);

export function StartNode({ data, selected }) {
  return (
    <Shell selected={selected} accent="var(--cares-green)" width={150}>
      <Title icon={Play} color="var(--cares-green)">{data.label || "Start"}</Title>
      <Handle type="source" position={Position.Right} style={PORT} />
    </Shell>
  );
}

export function EndNode({ data, selected }) {
  const rejected = String(data.outcome || "").toUpperCase().includes("REJECT");
  const accent = rejected ? "#ef4444" : "#64748b";
  return (
    <Shell selected={selected} accent={accent} width={150}>
      <Title icon={Square} color={accent}>{data.label || "End"}</Title>
      {data.outcome && (
        <Meta>
          <Chip tone={rejected ? "var(--cares-orange)" : "var(--cares-text-3)"} color={rejected ? "var(--cares-orange)" : "var(--cares-text-3)"}>
            {data.outcome}
          </Chip>
        </Meta>
      )}
      <Handle type="target" position={Position.Left} style={PORT} />
    </Shell>
  );
}

export function StageNode({ data, selected }) {
  const assignees = data.assignees || [];
  return (
    <Shell selected={selected} accent="var(--cares-purple)">
      <Title icon={Users} color="var(--cares-purple)">{data.label || "Stage"}</Title>
      <Meta>
        {data.stageKey
          ? <Chip tone="var(--cares-purple)" color="var(--cares-purple)">{data.stageKey}</Chip>
          // Surfaced on the node itself, not just in the validator: an unkeyed
          // stage is invisible to every screen that maps a record to the canvas.
          : <Chip tone="var(--cares-amber)" color="var(--cares-amber)">no stage key</Chip>}
        {data.slaHours ? <Chip>{data.slaHours}h SLA</Chip> : null}
      </Meta>
      <div style={{ marginTop: 7, display: "flex", alignItems: "center", gap: 4,
                    fontSize: 10, color: assignees.length ? "var(--cares-text-3)" : "var(--cares-amber)" }}>
        {assignees.length === 0 ? (
          <><AlertTriangle size={10} /> Nobody assigned</>
        ) : (
          <>
            <Users size={10} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis",
                           whiteSpace: "nowrap" }}>
              {assignees.map((a) => a.name).join(", ")}
            </span>
          </>
        )}
      </div>
      <Handle type="target" position={Position.Left} style={PORT} />
      <Handle type="source" position={Position.Right} style={PORT} />
    </Shell>
  );
}

export function DecisionNode({ data, selected }) {
  return (
    <Shell selected={selected} accent="var(--cares-amber)" width={175}>
      <Title icon={GitBranch} color="var(--cares-amber)">{data.label || "Decision"}</Title>
      <div style={{ marginTop: 5, fontSize: 9.5, color: "var(--cares-text-note)" }}>
        Routes on record data
      </div>
      <Handle type="target" position={Position.Left} style={PORT} />
      {/* One source port; each outgoing connection carries its own condition. */}
      <Handle type="source" position={Position.Right} style={{ ...PORT, background: "var(--cares-amber)" }} />
    </Shell>
  );
}

export function AutomationNode({ data, selected }) {
  return (
    <Shell selected={selected} accent="var(--cares-blue)" width={185}>
      <Title icon={Zap} color="var(--cares-blue)">{data.label || "Automation"}</Title>
      {data.action && <Meta><Chip tone="var(--cares-blue)" color="var(--cares-blue)">{data.action}</Chip></Meta>}
      <Handle type="target" position={Position.Left} style={PORT} />
      <Handle type="source" position={Position.Right} style={PORT} />
    </Shell>
  );
}

export const nodeTypes = {
  start: StartNode,
  stage: StageNode,
  decision: DecisionNode,
  automation: AutomationNode,
  end: EndNode,
};

export const NODE_ACCENT = {
  start: "#10b981", stage: "#4f46e5", decision: "#f59e0b",
  automation: "#0ea5e9", end: "#64748b",
};

export const NODE_ICON = {
  start: Play, stage: Users, decision: GitBranch, automation: Zap, end: Square,
};

export { Clock };
